import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import App from './App';
import './index.css';
import './fonts/Pretendard-1.3.6/public/variable/PretendardVariable.ttf';
import store from './store';
import { ToastProvider } from 'use-toast-mui';
import './i18n';
import { AppProvider } from './context/AppContext';
import { PermissionsProvider } from './routes/Permissions';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { errorMessages } from './utils/methods';

function QueryProvider({ children }) {
  const toast = useToast();
  const lastNetworkErrorRef = React.useRef(0);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: error => {
            const message = errorMessages(error);
            if (error?.response?.status === 401 || !message) return;
            // Network Error 중복 방지 (3초 내 동일 에러 무시)
            if (!error?.response) {
              const now = Date.now();
              if (now - lastNetworkErrorRef.current < 3000) return;
              lastNetworkErrorRef.current = now;
            }
            toast.error(message);
          },
        }),
        mutationCache: new MutationCache({
          onError: error => {
            // 사용자가 확인창에서 취소한 요청 (예: IoT 오프라인 강제 전송 취소)
            if (error?.silent) return;
            const message = errorMessages(error);
            if (error?.response?.status === 401 || !message) return;
            toast.error(message);
          },
        }),
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              // Network Error는 재시도 안 함
              if (!error?.response) return false;
              return failureCount < 1;
            },
            refetchOnWindowFocus: false,
            staleTime: 1000 * 30,
          },
        },
      }),
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <AppProvider>
      <ToastProvider autoHideDuration={3000} disableWindowBlurListener>
        <QueryProvider>
          <PermissionsProvider>
            <App />
          </PermissionsProvider>
        </QueryProvider>
      </ToastProvider>
    </AppProvider>
  </Provider>,
);
